<template>
    <!-- Seach and page options -->
    <div class="row d-flex align-items-center justify-content-between flex-wrap m-0 mt-4 pb-3 gap-2">
        <!-- Filter search container -->
        <div class="col-9 col-md-5 col-lg-3 searchInputContainer gap-2 rounded-pill bg-white border-gray-500">
            <!-- Search icon -->
            <i class="bi-search inline-icon text-body"></i>
            <div class="input-group">
                <!-- Search input -->
                <input :disabled="contentLoading" ref="searchInput" class="form-control p-0 border-0 bg-white active-border" type="text" v-model="search" placeholder="Type to filter" >
                <!-- Clear button -->
                <span v-if="!contentLoading && search && search.trim().length > 0" class="input-group-append" @click="clearSearchInput">
                    <i ref="clearIcon" class="bi bi-x inline-icon"></i>
                </span>
            </div>
        </div>
        <div class="d-flex gap-2 align-items-center p-0 w-auto">
            <select :disabled="contentLoading" class="form-select" v-model.number="itemsPerPage">
                <option>5</option>
                <option>10</option>
                <option>25</option>
                <option>50</option>
                <option>100</option>
                <option>500</option>
            </select>
        </div>
    </div>

    <!-- Divider -->
    <div class="divider sm mb-2"></div>

    <!-- Table -->
    <div class="table-responsive card border-bottom-0">
        <div class="overlay" v-if="contentLoading"></div>
        <table class="table align-middle mb-0 text-nowrap">
            <thead>
                <tr class="text-dark bg-grey-medium">
                    <template v-for="(column, index) in columnDef" :key="'row_header_' + index">
                        <th v-if="!column.hasOwnProperty('hideCondition') || column.hideCondition" class="fw-500" :class="column.header_class" :style="column.width ? 'width: '+column.width+'%' : ''">
                            {{ column.label?.toUpperCase() }}
                            <span class="bi ms-2 hoverable-icon cursor-pointer bg-gray-dark" v-if="column.sortable" @click="setSort(column.sortKey ? column.sortKey : column.key)" :class="{ 'bi-sort-alpha-down text-primary': (sortKey == column.sortKey || sortKey == column.key) && sortDir == 'asc', 'bi-sort-alpha-down-alt text-primary': (sortKey == column.sortKey || sortKey == column.key) && sortDir == 'desc', 'bi-sort-alpha-down text-muted': (sortKey !== column.sortKey && sortKey !== column.key) }"></span>
                        </th>
                    </template>
                </tr>
            </thead>
            <tbody>
                <template v-for="(rowData, rowIndex) in rowsToDisplay" :key="'row_template_' + rowIndex">
                    <tr>
                        <template v-for="(column, index) in columnDef" :key="'row_data_template_' + rowIndex">
                            <slot v-if="column.isSlot && (!column.hasOwnProperty('hideCondition') || column.hideCondition)" :name="column.key" v-bind="{ rowData, column }" :style="column.width ? 'width: '+column.width+'%' : ''" :key="'row_' + rowIndex + '_slot_' + index"></slot>
                            
                            <td v-else-if="rowData.hasOwnProperty(column.key) && (!column.hasOwnProperty('hideCondition') || column.hideCondition)" :class="column.class" :style="column.width ? 'width: '+column.width+'%' : ''" :key="'row_' + rowIndex + '_data_' + index">{{ column.hasOwnProperty('formatter') ? column.formatter(rowData[column.key], rowData) : rowData[column.key] }}</td>
                        </template>
                    </tr>
                </template>
                <div v-if="!contentLoading && totalItems == 0">No Records Found</div>
            </tbody>
        </table>
    </div>

    <!-- Pagination -->
    <pagination :contentLoading="contentLoading" :totalItems="totalItems" :currentPage="currentPage" :itemsPerPage="itemsPerPage" @update:currentPage="currentPage = $event"></pagination>
</template>

<script setup>
import { ref, watch, computed, watchEffect, onMounted } from "vue";
import Pagination from "./Pagination.vue";
import { useDebounceFn } from "@vueuse/core";
import apiClient from "@/utils/axios.js";
import { useHttpErrorManager } from '@/composables/useHttpErrorManager.js';

const props = defineProps({
    columnDef: Array,
    dataUrl: String,
    sort: String,
    sort_dir: String
});

const emits = defineEmits([
    'dataLoaded',
    'loadingData',
]);

const clearIcon = ref(null);
const searchInput = ref(null);
const search = ref('');
const itemsPerPage = ref(10);
const currentPage = ref(1);
const from = ref(0);
const to = ref(0);

const totalItems = ref(0);
const totalPages = computed(() => Math.ceil(totalItems.value / itemsPerPage.value));

const rowsToDisplay = ref([]);

watch(itemsPerPage, (newPerPage) => {
    if (currentPage.value > totalPages.value) {
        currentPage.value = 1;
    }
});

const clearSearchInput = () => {
    search.value = '';
    searchInput.value.blur();
}

const sortKey = ref(props.sort ? props.sort : "");
const sortDir = ref(props.sort_dir ? props.sort_dir : "asc");
const setSort = (val) => {
    if (sortKey.value == val) {
        if (sortDir.value == 'asc') {
            sortDir.value = 'desc';
        }
        else {
            sortDir.value = 'asc';
        }
    }
    else {
        sortKey.value = val;
        sortDir.value = 'asc';
    }
}

const contentLoading = ref(false);
const params = ref({});

const fetchData = async () => {
    emits('loadingData');
    if (props.dataUrl && !contentLoading.value) {
        contentLoading.value = true;
        try {

            const response = await apiClient.get(props.dataUrl, { params: params.value });
            if (response.data.tableData) {

                totalItems.value = response.data.tableData.total;
                from.value = response.data.tableData.from;
                if (from.value === null) {
                    from.value = 0;
                }
                to.value = response.data.tableData.to;
                if (to.value === null) {
                    to.value = 0;
                }

                rowsToDisplay.value = response.data.tableData.data;
            }

            contentLoading.value = false;
            emits('dataLoaded');
        }
        catch (error) {
            contentLoading.value = false;
            let errorObj = await useHttpErrorManager().handleError(error, false);
            emits('dataLoaded');
        }
    }
    else{
        emits('dataLoaded');
    }
}

watchEffect(() => {
    params.value['limit'] = itemsPerPage.value;
    params.value['page'] = currentPage.value;

    if (sortKey.value && sortDir.value) {
        params.value['dir'] = sortDir.value;
        params.value['sort'] = sortKey.value;
    }
    else {
        if (params.value.hasOwnProperty('dir')) {
            delete params.value['dir'];
        }
        if (params.value.hasOwnProperty('sort')) {
            delete params.value['sort'];
        }
    }
});

const debounceSearch = useDebounceFn(async () => {
    if (!contentLoading.value) {
        if(search.value && search.value.trim()){
            params.value['filter'] = search.value.trim();
        }
        else{
            delete params.value['filter'];
        }
        await fetchData();
    }
}, 800);

watch(search, (val) => {
    debounceSearch();
});

watch(params, (val) => {
    if (!contentLoading.value) {
        fetchData();
    }
}, { deep: true });

onMounted(() => {
    fetchData();
})

const getTotalItems = () => {
    if (totalItems.value) {
        return totalItems.value;
    }
    return 0;
}

const refresh = async () => {
    await fetchData();
}

defineExpose({
    refresh,
    getTotalItems
});

</script>

<style scoped lang="scss">
.searchInputContainer {
    &:focus-within {
        border: 1px solid #0f0f0f !important;
    }
}
</style>